import React, {FocusEvent, ChangeEvent, Fragment, useState} from 'react';
import {useFieldArray, useFormContext} from 'react-hook-form';
import b_ from 'b_';

import cloneDeep from 'lodash/cloneDeep';

import {Textinput, Tumbler} from 'client/common/components/lego';
import Block from 'client/common/components/block';
import BlockInput from 'client/common/components/block-input';
import SizeBlock from 'client/common/components/size-block';
import AudienceBlock from 'client/common/components/audience-block';

import {BordomatikBox} from 'client/common/types';

import I18N from 'assets/i18n/brandlift-project';

import SettingsBlock from './settings-block';
import NameBlock from './name-block';
import VideoBlock from './video-block';
import BrandUniqueBlock from './brand-unique-block';
import ParametersBlock from './parameters-block';
import LanguageBlock from './language-block';
import BottomButtons from './bottom-buttons';

import {OptionLists, setImmut, wrapArray} from './utils';

import ImageList from './image-list';
import {BordomatikFormProps, BordomatikFormValues} from './types';

import './creative-settings-form.css';

const b = b_.with('creative-settings-form');

const i18n = I18N.creatives.creativeSettings;

interface UseFormProps {
    box: BordomatikBox;
    updateSurveyBoxContext: (box: unknown) => Promise<void>;
}

// eslint-disable-next-line max-statements
function useFormProps(props: UseFormProps) {
    const {box, updateSurveyBoxContext} = props;

    const [packshotsEnabled, setPackshotsEnabled] = useState(box?.context?.optionalQuestions?.packshots ?? false);
    const [slogansEnabled, setSlogansEnabled] = useState(box?.context?.optionalQuestions?.slogans ?? false);
    const [castingEnabled, setCastingEnabled] = useState(box?.context?.optionalQuestions?.casting ?? false);

    const slogansField = useFieldArray({name: 'options.slogans'});
    const packshotsField = useFieldArray({name: 'options.packshots'});
    const castingField = useFieldArray({name: 'options.casting'});

    const handleBrandUpdate = async (e: FocusEvent<HTMLInputElement>) => {
        await updateSurveyBoxContext(setImmut(box, 'context.brand', e.target.value));
    };

    const handleCastingRoleUpdate = async (e: FocusEvent<HTMLInputElement>) => {
        await updateSurveyBoxContext(setImmut(box, 'context.castingRole', e.target.value));
    };

    const handlePackshotsUpdate = async (checked: boolean) => {
        setPackshotsEnabled(checked);
        await updateSurveyBoxContext(setImmut(box, 'context.optionalQuestions.packshots', checked));
    };

    const handleSlogansUpdate = async (checked: boolean) => {
        setSlogansEnabled(checked);
        await updateSurveyBoxContext(setImmut(box, 'context.optionalQuestions.slogans', checked));
    };

    const handleCastingUpdate = async (checked: boolean) => {
        setCastingEnabled(checked);
        await updateSurveyBoxContext(setImmut(box, 'context.optionalQuestions.casting', checked));
    };

    const handleAboutOptionsBlur = (index: number) => async (e: ChangeEvent<HTMLInputElement>) => {
        const texts = cloneDeep(box?.context?.options?.about ?? ['', '', '', '', '']);

        texts[index] = e.target.value;

        await updateSurveyBoxContext(setImmut(box, 'context.options.about', texts));
    };

    const handleImageOptionsBlur = (index: number) => async (e: ChangeEvent<HTMLInputElement>) => {
        const texts = cloneDeep(box?.context?.options?.image ?? ['', '', '', '', '']);

        texts[index] = e.target.value;

        await updateSurveyBoxContext(setImmut(box, 'context.options.image', texts));
    };

    const handleBrandOptionsBlur = (index: number) => async (e: ChangeEvent<HTMLInputElement>) => {
        const texts = cloneDeep(box?.context?.options?.brand ?? ['', '']);

        texts[index] = e.target.value;

        await updateSurveyBoxContext(setImmut(box, 'context.options.brand', texts));
    };

    const handleSlogansOptionsBlur = (index: number) => async (
        e: ChangeEvent<HTMLInputElement>
    ) => {
        if (!slogansEnabled) {
            await handleSlogansUpdate(true);
        }

        const texts = cloneDeep(box?.context?.options?.slogans ?? ['', '', '']);

        texts[index] = e.target.value;

        await updateSurveyBoxContext(setImmut(box, 'context.options.slogans', texts));
    };

    const handleSlogansOptionRemove = async (index: number) => {
        let texts = cloneDeep(box?.context?.options?.slogans ?? ['', '', '']);

        texts.splice(index, 1);

        texts = texts.filter(Boolean);

        if (!texts.length && slogansEnabled) {
            await handleSlogansUpdate(false);
        }

        await updateSurveyBoxContext(setImmut(box, 'context.options.slogans', texts));
    };

    const handlePackshotsImageChange = (index?: number) => async (imgSrc: string) => {
        if (!packshotsEnabled) {
            await handlePackshotsUpdate(true);
        }

        const texts = cloneDeep(box?.context?.options?.packshots ?? []);

        if (index === undefined) {
            texts.push(imgSrc);
            packshotsField.append({text: imgSrc});
        } else {
            texts[index] = imgSrc;
        }

        await updateSurveyBoxContext(setImmut(box, 'context.options.packshots', texts));
    };

    const handlePackshotsImageRemove = async (index: number) => {
        let texts = cloneDeep(box?.context?.options?.packshots ?? []);

        texts.splice(index, 1);

        texts = texts.filter(Boolean);

        if (!texts.length && packshotsEnabled) {
            await handlePackshotsUpdate(false);
        }

        await updateSurveyBoxContext(setImmut(box, 'context.options.packshots', texts));
    };

    const handleCastingImageChange = (index?: number) => async (imgSrc: string) => {
        if (!castingEnabled) {
            await handleCastingUpdate(true);
        }

        const texts = cloneDeep(box?.context?.options?.casting ?? []);

        if (index === undefined) {
            texts.push(imgSrc);
            castingField.append({text: imgSrc});
        } else {
            texts[index] = imgSrc;
        }

        await updateSurveyBoxContext(setImmut(box, 'context.options.casting', texts));
    };

    const handleCastingImageRemove = async (index: number) => {
        let texts = cloneDeep(box?.context?.options?.casting ?? []);

        texts.splice(index, 1);

        texts = texts.filter(Boolean);

        if (!texts.length && castingEnabled) {
            await handleCastingUpdate(false);
        }

        await updateSurveyBoxContext(setImmut(box, 'context.options.casting', texts));
    };

    return {
        packshotsEnabled,
        slogansEnabled,
        castingEnabled,

        fieldArrays: {
            packshots: packshotsField,
            casting: castingField,
            slogans: slogansField
        },

        updateHandlers: {
            brand: handleBrandUpdate,
            packshots: handlePackshotsUpdate,
            casting: handleCastingUpdate,
            slogans: handleSlogansUpdate,
            castingRole: handleCastingRoleUpdate
        },
        listHandlers: {
            about: handleAboutOptionsBlur,
            image: handleImageOptionsBlur,
            brand: handleBrandOptionsBlur,
            packshots: handlePackshotsImageChange,
            packshotsRemove: handlePackshotsImageRemove,
            casting: handleCastingImageChange,
            castingRemove: handleCastingImageRemove,
            slogans: handleSlogansOptionsBlur,
            slogansRemove: handleSlogansOptionRemove
        }
    };
}

// eslint-disable-next-line complexity
function BordomatikForm(props: BordomatikFormProps) {
    const {state, slug, box, disabled = false, creative, updateSurveyBoxContext} = props;

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const {register, control, errors} = useFormContext<BordomatikFormValues>();

    const {
        packshotsEnabled,
        slogansEnabled,
        castingEnabled,
        updateHandlers,
        listHandlers,
        fieldArrays
    } = useFormProps({
        box,
        updateSurveyBoxContext
    });

    const commonBlockProps = {box, slug, disabled, creative, state, register, errors, control, updateSurveyBoxContext};

    return (
        <Fragment>
            <LanguageBlock />
            <NameBlock {...commonBlockProps} />
            <VideoBlock
                label={i18n.new.video.label}
                description={i18n.new.video.desc}
                {...commonBlockProps}
            />
            <SettingsBlock handleBrandUpdate={updateHandlers.brand} {...commonBlockProps} />
            <AudienceBlock />
            <SizeBlock description={i18n.sizeBlock.description} />
            <BrandUniqueBlock {...commonBlockProps} />
            <Block
                header={i18n.new.extraTests.label}
                description={i18n.new.extraTests.desc}
            >
                <div className={b('sub-block')}>
                    <Tumbler
                        checked={castingEnabled}
                        labelAfter={i18n.additionalQuestions.casting}
                        onChange={e => updateHandlers.casting(e.target.checked)}
                        size='m'
                        disabled={disabled}
                        view='dl'
                    />
                    {castingEnabled && <>
                        <BlockInput title={i18n.questionText.castingRole} error={errors.castingRole}>
                            <Textinput
                                size='m'
                                name='castingRole'
                                placeholder={i18n.placeholders.castingRoleInput}
                                onBlur={updateHandlers.castingRole}
                                disabled={disabled}
                                view='dl'
                                controlRef={register({
                                    required: i18n.validationMsg.castingRole
                                })}
                                state={errors.castingRole && 'error'}
                            />
                        </BlockInput>
                        <BlockInput title={i18n.questionText.casting}>
                            <ImageList
                                uploadBtnText={i18n.addBtnText.casting}
                                register={register}
                                errors={errors}
                                rules={{
                                    required: i18n.optionValidationMsg.casting
                                }}
                                fields={fieldArrays.casting.fields}
                                fieldName='options.casting'
                                disabled={disabled}
                                onBlur={listHandlers.casting}
                                onRemove={async (index: number) => {
                                    await listHandlers.castingRemove(index);

                                    fieldArrays.casting.remove(index);
                                }}
                                minItems={1}
                                maxItems={3}
                            />
                        </BlockInput>
                    </>}
                </div>
                <div className={b('sub-block')}>
                    <Tumbler
                        checked={slogansEnabled}
                        labelAfter={i18n.additionalQuestions.slogans}
                        onChange={e => updateHandlers.slogans(e.target.checked)}
                        size='m'
                        disabled={disabled}
                        view='dl'
                    />
                    {slogansEnabled && (
                        <BlockInput title={i18n.questionText.slogans}>
                            <OptionLists
                                register={register}
                                errors={errors}
                                rules={{
                                    required: i18n.optionValidationMsg.slogans
                                }}
                                fields={fieldArrays.slogans.fields}
                                fieldName='options.slogans'
                                disabled={disabled}
                                onBlur={listHandlers.slogans}
                                placeholderPrefix={i18n.placeholderPrefix.slogans}
                                minItems={1}
                                maxItems={3}
                                onAppend={fieldArrays.slogans.append}
                                onRemove={async (index: number) => {
                                    await listHandlers.slogansRemove(index);

                                    fieldArrays.slogans.remove(index);
                                }}
                                addItemBtnText={i18n.addBtnText.slogans}
                                hasNoOpinion
                            />
                        </BlockInput>
                    )}
                </div>
                <div className={b('sub-block')}>
                    <Tumbler
                        checked={packshotsEnabled}
                        labelAfter={i18n.additionalQuestions.packshots}
                        onChange={e => updateHandlers.packshots(e.target.checked)}
                        size='m'
                        disabled={disabled}
                        view='dl'
                    />
                    {packshotsEnabled && (
                        <BlockInput title={i18n.questionText.packshots}>
                            <ImageList
                                uploadBtnText={i18n.addBtnText.packshots}
                                register={register}
                                errors={errors}
                                rules={{
                                    required: i18n.optionValidationMsg.packshots
                                }}
                                fields={fieldArrays.packshots.fields}
                                fieldName='options.packshots'
                                disabled={disabled}
                                onBlur={listHandlers.packshots}
                                onRemove={async (index: number) => {
                                    await listHandlers.packshotsRemove(index);

                                    fieldArrays.packshots.remove(index);
                                }}
                                minItems={1}
                                maxItems={3}
                            />
                        </BlockInput>
                    )}
                </div>
            </Block>
            <ParametersBlock box={box}>
                <div className={b('separator')} />
                <div className={b('desc')}>{i18n.new.parameters.alsoDesc}</div>
                <div className={b('parameters')}>
                    <div className={b('parameter')}>{i18n.new.parameters.also.gender}</div>
                    <div className={b('parameter')}>{i18n.new.parameters.also.age}</div>
                    <div className={b('parameter')}>{i18n.new.parameters.also.address}</div>
                    <div className={b('parameter')}>{i18n.new.parameters.also.interests}</div>
                </div>
            </ParametersBlock>
            <BottomButtons {...props} />
        </Fragment>
    );
}

BordomatikForm.getDefaultValue = (
    box: BordomatikBox,
    name: string
): BordomatikFormValues => {
    const options = box?.context?.options;

    return {
        creativeName: name ?? '',
        brandName: box?.context?.brand ?? '',
        industry: box?.context?.industry,
        castingRole: box?.context?.castingRole ?? '',
        options: {
            about: wrapArray(options?.about ?? ['', '', '', '', '']),
            image: wrapArray(options?.image ?? ['', '', '', '', '']),
            brand: wrapArray(options?.brand ?? ['', '']),
            slogans: wrapArray(options?.slogans ?? ['', '', '']),
            packshots: wrapArray(options?.packshots ?? []),
            casting: wrapArray(options?.casting ?? [])
        }
    };
};

export default BordomatikForm;
